import { CashbackTransaction } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useCashbackTransactions from 'features/rewards/hooks/useCashbackTransactions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import ExpressiveSmText from 'shared/components/vxl/typography/expressive/ExpressiveSmText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import {
  showZendeskArticle,
  ZendeskArticles,
} from 'shared/utils/zendesk/zendeskUtils';

const CashbackEarnedScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'myActivity',
  });

  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();

  const { loading, error, cashbackTransactions } = useCashbackTransactions();

  const handleSupportPressAnalytics = usePressableAnalytics({
    analyticsId: 'cashbackFaqClicked',
  });

  const handleBackArrowNavigation = () => {
    router.back();
  };

  // Create date-based groups for transaction listing
  // This is being used only on this screen, so it is defined here
  // If any use case arises to use this elsewhere, we can move it to a helper/util file
  function groupTransactionsByMonth(
    transactions: { node: CashbackTransaction }[],
  ) {
    const groups: Record<string, CashbackTransaction[]> = {};
    transactions.forEach(({ node }) => {
      if (!node.transactionDate) {
        return;
      }
      const date = new Date(node.transactionDate);
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      const key = `${month} ${year}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(node);
    });
    return groups;
  }

  const groupedTransactions = cashbackTransactions?.transactions?.edges
    ? groupTransactionsByMonth(cashbackTransactions.transactions.edges)
    : {};

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              trailingAccessories={
                <IconButton
                  iconKey="QUESTION_MARK"
                  iconSize="MD"
                  iconButtonAccessibilityLabel="QUESTION_MARK"
                  iconOnPress={() => {
                    handleSupportPressAnalytics();
                    showZendeskArticle(ZendeskArticles?.CASHBACK);
                  }}
                  testID="question-mark-icon"
                />
              }
              primaryOnPress={handleBackArrowNavigation}
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: () => router.navigate('/rewards'),
              }}
              screenAnalyticsEventKey="rewardsError"
            />
          ) : null
        }
      />
      {!cashbackTransactions?.transactions?.edges?.length ? (
        <EmptyStateScreen
          headlineText={translatedText('noCashbackTitle')}
          bodyText={translatedText('noCashbackMessage')}
          illustrationKey="CARD_C"
        />
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: bottom }}>
          <View style={styles.cashbackContainer}>
            <BodySmBoldText>
              {translatedText('lifetimeEarnings')}
            </BodySmBoldText>
            <ExpressiveSmText
              style={styles.centeredText}
              testID="currentBalance"
            >
              {cashbackTransactions.earningsUpToDate.formatted}
            </ExpressiveSmText>
          </View>
          <View style={styles.separator} />
          <View>
            {Object.entries(groupedTransactions).map(([monthYear, nodes]) => (
              <View key={monthYear}>
                <BodySmBoldText style={styles.sectionHeader}>
                  {monthYear}
                </BodySmBoldText>
                {nodes.map((transaction, idx) => {
                  const isLastItem = idx === nodes.length - 1;
                  return (
                    <View key={transaction.id}>
                      <TransactionListItem
                        titleText={transaction.title}
                        secondaryText={transaction.transactionDate}
                        amountText={
                          transaction.cashbackAmount?.formattedWithPlusPrefix
                        }
                        amountStyle={
                          transaction.isCashbackTransaction
                            ? 'POSITIVE'
                            : 'NEGATIVE'
                        }
                        isPending={false}
                        iconAvatar={{
                          variant: 'MD',
                          imageSource: { uri: transaction.iconUrl },
                        }}
                        onPress={() => {
                          router.push({
                            pathname: `./transactions/${transaction.id}`,
                          });
                        }}
                        analyticsId="cashbackDetailClicked"
                      />
                      {!isLastItem ? <HairLineDivider /> : null}
                    </View>
                  );
                })}
              </View>
            ))}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  cashbackContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    justifyContent: 'center',
    paddingTop: theme.spacing.xl,
    paddingBottom: theme.spacing.xxl,
  },
  centeredText: {
    textAlign: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  sectionHeader: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  separator: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
});

export default CashbackEarnedScreen;
